import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, X, UserPlus } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * DemoBanner - Shows a persistent banner for demo users
 * 
 * Features:
 * - Shows remaining time until demo expires
 * - Sign up CTA to convert demo users
 * - Dismissable (but re-appears on next page)
 */
const DemoBanner = () => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('');
  
  // Check if this is a demo session - read synchronously from localStorage
  const isDemoSession = typeof window !== 'undefined' && 
    localStorage.getItem('is_demo_session') === 'true';

  useEffect(() => {
    if (!isDemoSession) return;

    const updateTimeRemaining = () => {
      const expiresAt = localStorage.getItem('demo_expires_at');
      if (!expiresAt) return;

      const expiry = new Date(expiresAt);
      const now = new Date();
      const diffMs = expiry - now;

      if (diffMs <= 0) {
        setTimeRemaining('Expired');
        return;
      }

      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

      if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m`);
      } else {
        setTimeRemaining(`${minutes}m`);
      }
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [isDemoSession]);

  if (!isDemoSession || dismissed) return null;

  return (
    <div 
      className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-amber-500 to-orange-500 text-white py-2 px-4 shadow-lg"
      data-testid="demo-banner"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
            <Clock className="w-4 h-4" />
            <span>Demo Mode</span>
          </div>
          <span className="text-sm">
            Session expires in <strong>{timeRemaining}</strong>
          </span>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            className="bg-white text-orange-600 hover:bg-orange-50"
            onClick={() => navigate('/signup')}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Sign Up Free
          </Button>
          <button
            onClick={() => setDismissed(true)}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            aria-label="Dismiss banner"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DemoBanner;
